import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useFriends } from '../context/FriendContext';
import Icon from '../components/Icon';
import { F1_DRIVERS, driverImgUrl } from '../constants/f1Drivers';
import { getLevelInfo, getGrade } from '../utils/xpUtils';
import ChatModal from '../components/ChatModal';
import ChallengeModal from '../components/ChallengeModal';

// ── Shared helpers ────────────────────────────────────────────────────────────
const UserAvatar = ({ user, size = 8 }) => {
  const [failed, setFailed] = useState(false);
  const initials = (user?.username || '?').slice(0, 2).toUpperCase();
  if (user?.avatar && !failed)
    return <img src={user.avatar} alt={user.username}
      className={`w-${size} h-${size} rounded-full object-cover bg-zinc-800 shrink-0`}
      onError={() => setFailed(true)} />;
  return (
    <div className={`w-${size} h-${size} rounded-full bg-f1red/20 border border-f1red/40 flex items-center justify-center shrink-0`}>
      <span className="text-f1red font-black text-xs">{initials}</span>
    </div>
  );
};

// ── Score breakdown helpers ────────────────────────────────────────────────────
const VALID_BOOSTS = [1, 2, 5, 10];
const getB = (boosts, key) => VALID_BOOSTS.includes(boosts?.[key]) ? boosts[key] : 1;
const applyBoost = (base, isCorrect, mult) =>
  mult === 1 ? (isCorrect ? base : 0) : (isCorrect ? base * mult : -Math.round(base / mult));
const norm = (v) => v ? String(v).toUpperCase().trim() : v;

const computeLines = (picks, results, boosts = {}) => {
  if (!picks || !results) return [];
  const lines = [];
  const add = (key, base, label, value, correct) => {
    const mult = getB(boosts, key);
    const pts = applyBoost(base, correct, mult);
    if (pts !== 0 || correct) lines.push({ label, value: String(value || '').toUpperCase(), pts, correct, boosted: mult > 1 });
  };
  if (picks.winner)               add('winner',               25, 'Vainqueur',         picks.winner,               norm(picks.winner)               === norm(results.winner));
  if (picks.p2)                   add('p2',                   18, 'P2',                picks.p2,                   norm(picks.p2)                   === norm(results.p2));
  if (picks.p3)                   add('p3',                   15, 'P3',                picks.p3,                   norm(picks.p3)                   === norm(results.p3));
  if (picks.fastestLap)           add('fastestLap',           12, 'Meilleur tour',     picks.fastestLap,           norm(picks.fastestLap)           === norm(results.fastestLap));
  if (picks.polePosition)         add('polePosition',         10, 'Pole position',     picks.polePosition,         norm(picks.polePosition)         === norm(results.polePosition));
  if (picks.fastestQualifyingLap) add('fastestQualifyingLap',  8, 'Meilleur tour Q',   picks.fastestQualifyingLap, norm(picks.fastestQualifyingLap) === norm(results.fastestQualifyingLap));
  if (results.safetyCar        != null && picks.safetyCar        != null) add('safetyCar',        4,  'Safety Car',      picks.safetyCar        ? 'Oui' : 'Non', picks.safetyCar        === results.safetyCar);
  if (results.virtualSafetyCar != null && picks.virtualSafetyCar != null) add('virtualSafetyCar', 2,  'Virtual SC',      picks.virtualSafetyCar ? 'Oui' : 'Non', picks.virtualSafetyCar === results.virtualSafetyCar);
  if (results.redFlag          != null && picks.redFlag          != null) add('redFlag',          10, 'Drapeau rouge',   picks.redFlag          ? 'Oui' : 'Non', picks.redFlag          === results.redFlag);
  const q1 = (picks.q1Eliminated || []).filter(d => (results.q1Eliminated || []).some(r => norm(r) === norm(d)));
  const q2 = (picks.q2Eliminated || []).filter(d => (results.q2Eliminated || []).some(r => norm(r) === norm(d)));
  if (q1.length) lines.push({ label: 'Q1 éliminés', value: q1.map(d => d.toUpperCase()).join(', '), pts: q1.length * getB(boosts, 'q1Eliminated'), correct: true, boosted: getB(boosts, 'q1Eliminated') > 1 });
  if (q2.length) lines.push({ label: 'Q2 éliminés', value: q2.map(d => d.toUpperCase()).join(', '), pts: q2.length * 2 * getB(boosts, 'q2Eliminated'), correct: true, boosted: getB(boosts, 'q2Eliminated') > 1 });
  return lines;
};

const computeSprintLines = (sprintPicks, sprintResults) => {
  if (!sprintPicks || !sprintResults) return [];
  const lines = [];
  if (sprintPicks.winner && norm(sprintPicks.winner) === norm(sprintResults.winner)) lines.push({ label: 'Sprint Vainqueur', value: norm(sprintPicks.winner), pts: 12, correct: true });
  if (sprintPicks.p2     && norm(sprintPicks.p2)     === norm(sprintResults.p2))     lines.push({ label: 'Sprint P2',        value: norm(sprintPicks.p2),     pts: 10, correct: true });
  if (sprintPicks.p3     && norm(sprintPicks.p3)     === norm(sprintResults.p3))     lines.push({ label: 'Sprint P3',        value: norm(sprintPicks.p3),     pts:  8, correct: true });
  return lines;
};

const REACTION_EMOJIS = [
  { key: 'laugh', emoji: '😂' },
  { key: 'cry',   emoji: '😭' },
  { key: 'angry', emoji: '😤' },
  { key: 'thumb', emoji: '👍' },
];

// ── Player score breakdown modal ──────────────────────────────────────────────
const PlayerScoreModal = ({ entry, rank, onClose }) => {
  const { user: me } = useAuth();
  const { isFriend } = useFriends() || {};
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('driver');
  const [reactionCounts, setReactionCounts] = useState({ laugh: 0, cry: 0, angry: 0, thumb: 0 });
  const [myReaction, setMyReaction] = useState(null);
  const [reacting, setReacting] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [challengeOpen, setChallengeOpen] = useState(false);
  const isOwnProfile = me && entry.user._id === me.id;
  const isAmis = !isOwnProfile && isFriend?.(entry.user._id);

  useEffect(() => {
    api.get(`/users/${entry.user._id}`)
      .then(r => {
        setProfile(r.data);
        if (r.data.reactionCounts) setReactionCounts(r.data.reactionCounts);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    if (!isOwnProfile) {
      api.get(`/users/${entry.user._id}/my-reaction`)
        .then(r => setMyReaction(r.data.mine || null))
        .catch(() => {});
    }
  }, [entry.user._id]);

  const handleReact = async (key) => {
    if (isOwnProfile || reacting) return;
    setReacting(true);
    try {
      const { data } = await api.put(`/users/${entry.user._id}/react`, { emoji: key });
      setReactionCounts(data.counts);
      setMyReaction(data.mine);
    } catch {}
    setReacting(false);
  };

  const sd = entry.user.seasonDriver;
  const f1d = sd ? F1_DRIVERS.find(d => d.code === sd.code || d.name === sd.name) : null;
  const sdPts = entry.user.seasonDriverPoints || 0;
  const accentColor = f1d?.color || '#e10600';

  const driverBreakdown = profile?.seasonDriverBreakdown || [];
  const pronosRaces = (profile?.pronostics || [])
    .filter(p => p.score != null || p.sprintScore != null)
    .map(p => {
      const lines = computeLines(p.picks, p.race?.results, p.boosts);
      const sprintLines = computeSprintLines(p.sprintPicks, p.race?.sprintResults);
      return { ...p, lines, sprintLines, total: (p.score || 0) + (p.sprintScore || 0) };
    })
    .sort((a, b) => (a.race?.round || 0) - (b.race?.round || 0));
  const pronosTotal = pronosRaces.reduce((a, p) => a + p.total, 0);

  const xpInfo = getLevelInfo(entry.user?.xp ?? 0);
  const grade = getGrade(xpInfo.level);
  const avg = pronosRaces.length > 0 ? Math.round(pronosTotal / pronosRaces.length) : null;

  const rankLabel = rank === 1 ? 'P1' : rank === 2 ? 'P2' : rank === 3 ? 'P3' : `P${rank}`;
  const rankColor = rank === 1 ? '#FFD600' : rank === 2 ? '#A8A8B3' : rank === 3 ? '#CD7F32' : '#6b7280';

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
        style={{ background: 'rgba(0,0,0,0.80)', backdropFilter: 'blur(6px)' }}
        onClick={onClose}>
        <div className="relative w-full sm:max-w-md bg-zinc-950 border rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
          style={{ borderColor: accentColor + '44' }}
          onClick={e => e.stopPropagation()}>

          {/* Close — always on top */}
          <button onClick={onClose} type="button"
            className="absolute top-3 right-3 z-[60] w-8 h-8 rounded-full bg-black/60 flex items-center justify-center text-zinc-300 hover:text-white hover:bg-black/80 transition-colors">
            <Icon name="x" size={15} />
          </button>

          {/* ── HERO ── */}
          <div className="relative shrink-0 overflow-hidden" style={{ minHeight: 140 }}>
            {/* Driver image background */}
            {f1d && (
              <div className="absolute inset-0 flex items-end justify-end pointer-events-none">
                <img src={driverImgUrl(f1d.img)} alt=""
                  className="h-full max-h-48 object-contain object-bottom opacity-25 translate-x-4"
                  style={{ filter: `drop-shadow(0 0 24px ${accentColor}88)` }}
                  onError={e => { e.target.style.display = 'none'; }} />
              </div>
            )}
            {/* Gradient overlay */}
            <div className="absolute inset-0 pointer-events-none"
              style={{ background: `linear-gradient(135deg, ${accentColor}22 0%, transparent 60%), linear-gradient(to right, #09090b 50%, transparent 100%)` }} />
            {/* Bottom fade */}
            <div className="absolute bottom-0 left-0 right-0 h-10 pointer-events-none"
              style={{ background: 'linear-gradient(to bottom, transparent, #09090b)' }} />

            {/* Content */}
            <div className="relative z-10 px-5 pt-5 pb-4 flex items-end gap-4">
              {/* Avatar */}
              <div className="relative shrink-0">
                <div className="w-16 h-16 rounded-2xl overflow-hidden border-2"
                  style={{ borderColor: accentColor + '88' }}>
                  {entry.user.avatar
                    ? <img src={entry.user.avatar} alt={entry.user.username} className="w-full h-full object-cover" />
                    : <div className="w-full h-full flex items-center justify-center text-2xl font-black"
                        style={{ background: accentColor + '22', color: accentColor }}>
                        {(entry.user.username || '?').slice(0, 2).toUpperCase()}
                      </div>
                  }
                </div>
                {/* Rank badge */}
                <div className="absolute -bottom-2 -right-2 text-[11px] font-black px-1.5 py-0.5 rounded-lg border"
                  style={{ background: rankColor + '22', color: rankColor, borderColor: rankColor + '66' }}>
                  {rankLabel}
                </div>
              </div>

              {/* Name + grade */}
              <div className="flex-1 min-w-0 pb-1">
                <h2 className="text-xl font-black text-white leading-tight truncate">{entry.user.username}</h2>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded"
                    style={{ color: grade.color, background: grade.bg }}>{grade.label} · Niv. {xpInfo.level}</span>
                  {f1d && (
                    <span className="text-[10px] font-bold truncate" style={{ color: accentColor }}>
                      {f1d.name} · {f1d.team}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ── XP BAR ── */}
          <div className="px-5 pb-3 shrink-0">
            <div className="flex justify-between text-[10px] text-zinc-500 mb-1">
              <span>Niv. {xpInfo.level}</span>
              <span>{(entry.user.xp ?? 0).toLocaleString()} XP · encore {(xpInfo.nextLevelXP - xpInfo.currentXP).toLocaleString()} XP</span>
            </div>
            <div className="h-1.5 rounded-full bg-zinc-800 overflow-hidden">
              <div className="h-full rounded-full transition-all duration-700"
                style={{ width: `${Math.min(xpInfo.progress * 100, 100)}%`, background: `linear-gradient(90deg, ${accentColor}99, ${accentColor})` }} />
            </div>
          </div>

          {/* ── STATS ── */}
          <div className="mx-5 mb-3 grid grid-cols-3 divide-x divide-zinc-800 rounded-xl border border-zinc-800 bg-zinc-900/60 shrink-0">
            {[
              ['🏆', entry.totalScore.toLocaleString(), 'Points total'],
              ['📋', pronosRaces.length, 'GP scorés'],
              ['⌀', avg ?? '—', 'Moy / course'],
            ].map(([icon, val, label]) => (
              <div key={label} className="flex flex-col items-center py-2.5 px-1">
                <span className="text-base leading-none mb-0.5">{icon}</span>
                <span className="text-sm font-black text-white">{val}</span>
                <span className="text-[9px] text-zinc-500 mt-0.5 text-center leading-tight">{label}</span>
              </div>
            ))}
          </div>

          {/* ── FRIEND ACTIONS ── */}
          {!isOwnProfile && (
            <div className="px-5 mb-3 shrink-0 flex gap-2">
              {isAmis ? (
                <>
                  <button onClick={() => setChatOpen(true)}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-bold border border-zinc-700 bg-zinc-800 text-zinc-200 hover:border-zinc-500 transition-colors">
                    <Icon name="message" size={13} /> Message
                  </button>
                  <button onClick={() => setChallengeOpen(true)}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-bold border transition-colors"
                    style={{ borderColor: accentColor + '66', background: accentColor + '12', color: accentColor }}>
                    <Icon name="sword" size={13} /> Défier
                  </button>
                </>
              ) : (
                <Link to={`/joueurs/${entry.user._id}`} onClick={onClose}
                  className="flex-1 text-center py-2 rounded-xl text-xs font-bold border border-zinc-700 bg-zinc-800/60 text-zinc-300 hover:text-white hover:border-zinc-500 transition-colors">
                  Voir le profil complet →
                </Link>
              )}
            </div>
          )}

          {/* ── TABS ── */}
          <div className="flex border-y border-zinc-800 shrink-0">
            <button onClick={() => setTab('driver')}
              className={`flex-1 py-2.5 text-xs font-bold transition-colors ${tab === 'driver' ? 'text-white border-b-2' : 'text-zinc-500 hover:text-zinc-300'}`}
              style={tab === 'driver' ? { borderBottomColor: accentColor } : {}}>
              🏎 {f1d ? f1d.name.split(' ').at(-1) : 'Pilote'} · {sdPts} pts
            </button>
            <button onClick={() => setTab('pronos')}
              className={`flex-1 py-2.5 text-xs font-bold transition-colors ${tab === 'pronos' ? 'text-white border-b-2' : 'text-zinc-500 hover:text-zinc-300'}`}
              style={tab === 'pronos' ? { borderBottomColor: accentColor } : {}}>
              📋 Pronostics · {pronosTotal} pts
            </button>
          </div>

          {/* ── BODY (tabs content) ── */}
          <div className="overflow-y-auto flex-1 px-4 py-2">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-10 gap-3">
                <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: accentColor }} />
                <span className="text-xs text-zinc-500">Chargement…</span>
              </div>
            ) : tab === 'driver' ? (
              driverBreakdown.length === 0 ? (
                <p className="text-center text-zinc-500 py-8 text-sm">
                  {f1d ? `${f1d.name} n'a pas encore scoré cette saison.` : 'Aucun pilote de saison.'}
                </p>
              ) : (
                driverBreakdown.map((race, i) => (
                  <div key={i} className="border-b border-zinc-800/60 last:border-0 py-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <div>
                        <p className="text-sm font-black text-white">{race.raceName}</p>
                        <p className="text-[10px] text-zinc-600">Round {race.round}</p>
                      </div>
                      <span className="text-base font-black shrink-0" style={{ color: accentColor }}>
                        +{race.pts} pts
                      </span>
                    </div>
                    <div className="pl-2 border-l-2 space-y-0.5" style={{ borderColor: accentColor + '55' }}>
                      {race.lines.map((line, j) => (
                        <div key={j} className="flex items-center justify-between gap-2 py-0.5">
                          <span className="text-xs text-zinc-300">{line.label}</span>
                          <span className="text-xs font-black text-white">+{line.pts} pts</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )
            ) : (
              pronosRaces.length === 0 ? (
                <p className="text-center text-zinc-500 py-8 text-sm">Aucun pronostic scoré.</p>
              ) : (
                pronosRaces.map(p => (
                  <div key={p._id} className="border-b border-zinc-800/60 last:border-0 py-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <div>
                        <p className="text-sm font-black text-white">{p.race?.raceName || '—'}</p>
                        <p className="text-[10px] text-zinc-600">Round {p.race?.round}</p>
                      </div>
                      <span className={`text-base font-black shrink-0 ${p.total > 0 ? 'text-f1red' : 'text-zinc-600'}`}>
                        {p.total > 0 ? `+${p.total}` : p.total} pts
                      </span>
                    </div>
                    {p.lines.length > 0 && (
                      <div className="pl-2 border-l-2 border-zinc-700 space-y-0.5">
                        {p.lines.map((line, i) => (
                          <div key={i} className="flex items-center justify-between gap-2 py-0.5">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <span className={`text-xs shrink-0 ${line.correct ? 'text-green-400' : 'text-red-400'}`}>{line.correct ? '✓' : '✗'}</span>
                              <span className="text-xs text-zinc-400 shrink-0">{line.label}</span>
                              <span className="text-xs text-white font-medium truncate">· {line.value}</span>
                              {line.boosted && <span className="text-[9px] bg-yellow-400/20 text-yellow-400 px-1 rounded shrink-0">BOOST</span>}
                            </div>
                            <span className={`text-xs font-black shrink-0 ${line.pts > 0 ? 'text-white' : 'text-red-400'}`}>
                              {line.pts > 0 ? `+${line.pts}` : line.pts} pts
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    {p.sprintLines.length > 0 && (
                      <div className="pl-2 border-l-2 border-orange-800/50 space-y-0.5 mt-1.5">
                        {p.sprintLines.map((line, i) => (
                          <div key={i} className="flex items-center justify-between gap-2 py-0.5">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <span className="text-xs text-orange-400 shrink-0">✓</span>
                              <span className="text-xs text-orange-400/70 shrink-0">{line.label}</span>
                              <span className="text-xs text-white font-medium truncate">· {line.value}</span>
                            </div>
                            <span className="text-xs font-black text-orange-400 shrink-0">+{line.pts} pts</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {p.lines.length === 0 && p.sprintLines.length === 0 && (
                      <p className="text-[11px] text-zinc-600 pl-2 italic">Aucun pronostic correct</p>
                    )}
                  </div>
                ))
              )
            )}
          </div>

          {/* ── FOOTER — reactions ── */}
          {!isOwnProfile && (
            <div className="px-4 py-3 border-t border-zinc-800 shrink-0 bg-zinc-950">
              <div className="flex items-center justify-center gap-2">
                {REACTION_EMOJIS.map(({ key, emoji }) => {
                  const count = reactionCounts[key] || 0;
                  const isMe = myReaction === key;
                  return (
                    <button key={key} onClick={() => handleReact(key)} disabled={reacting}
                      className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl transition-all select-none ${
                        isMe ? 'scale-110' : 'hover:scale-105'
                      }`}
                      style={isMe
                        ? { background: accentColor + '20', border: `1px solid ${accentColor}60` }
                        : { background: '#27272a', border: '1px solid #3f3f46' }}>
                      <span className="text-xl leading-none">{emoji}</span>
                      <span className={`text-[10px] font-black`}
                        style={{ color: isMe ? accentColor : '#6b7280' }}>
                        {count > 0 ? count : ''}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {chatOpen && profile && <ChatModal friend={profile} onClose={() => setChatOpen(false)} />}
      {challengeOpen && profile && <ChallengeModal friend={profile} onClose={() => setChallengeOpen(false)} />}
    </>
  );
};

// ── CLASSEMENTS tab ───────────────────────────────────────────────────────────
const PODIUM_CFG = [
  { rank: 2, color: '#A8A8B3', glow: 'rgba(168,168,179,0.35)', blockH: 80,  avatarRem: 3.5 },
  { rank: 1, color: '#FFD600', glow: 'rgba(255,214,0,0.45)',   blockH: 110, avatarRem: 4.5 },
  { rank: 3, color: '#CD7F32', glow: 'rgba(205,127,50,0.3)',   blockH: 56,  avatarRem: 3   },
];

const CrownSVG = () => (
  <svg viewBox="0 0 44 30" width="38" height="26" xmlns="http://www.w3.org/2000/svg">
    <path d="M2,24 L7,8 L16,18 L22,2 L28,18 L37,8 L42,24 Z" fill="#FFD600" />
    <rect x="2" y="22" width="40" height="7" rx="2.5" fill="#FFD600" />
    <path d="M2,24 L7,8 L16,18 L22,2 L28,18 L37,8 L42,24 Z" fill="url(#cg)" opacity="0.4" />
    <defs>
      <linearGradient id="cg" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#fff" />
        <stop offset="100%" stopColor="transparent" />
      </linearGradient>
    </defs>
    <circle cx="22" cy="14" r="3"   fill="#fff"    opacity="0.95" />
    <circle cx="7"  cy="21" r="2"   fill="#fff"    opacity="0.75" />
    <circle cx="37" cy="21" r="2"   fill="#fff"    opacity="0.75" />
    <circle cx="22" cy="26" r="1.5" fill="#FFF176" opacity="0.8" />
  </svg>
);

const P1_STYLES = `
  @keyframes crown-float {
    0%,100% { transform: translateY(0px) rotate(-3deg); }
    50%      { transform: translateY(-6px) rotate(3deg); }
  }
  @keyframes crown-glow {
    0%,100% { filter: drop-shadow(0 0 5px #FFD600bb) drop-shadow(0 2px 10px #FFD60066); }
    50%     { filter: drop-shadow(0 0 14px #FFD600ff) drop-shadow(0 0 28px #FFD600cc) drop-shadow(0 0 50px #FFD60044); }
  }
  @keyframes p1-ring {
    0%,100% { box-shadow: 0 0 18px rgba(255,214,0,0.5), 0 0 36px rgba(255,214,0,0.2); }
    50%     { box-shadow: 0 0 32px rgba(255,214,0,0.9), 0 0 64px rgba(255,214,0,0.45), 0 0 90px rgba(255,214,0,0.15); }
  }
  @keyframes sparkle {
    0%,100% { opacity:0; transform:scale(0) rotate(0deg); }
    40%     { opacity:1; transform:scale(1) rotate(120deg); }
    70%     { opacity:0.6; transform:scale(0.8) rotate(240deg); }
  }
  @keyframes p1-block-shimmer {
    0%   { background-position: -200% center; }
    100% { background-position: 200% center; }
  }
`;

const SPARKLE_POS = [
  { top: '-8px',  left: '-10px',  delay: '0s'   },
  { top: '-12px', left: '50%',    delay: '0.4s' },
  { top: '-8px',  right: '-10px', delay: '0.8s' },
  { top: '30%',   left: '-14px',  delay: '0.6s' },
  { top: '30%',   right: '-14px', delay: '1.1s' },
  { top: '60%',   left: '-8px',   delay: '0.2s' },
];

const PodiumSlot = ({ entry, cfg, isMe, onClick }) => {
  const { level } = getLevelInfo(entry.user?.xp ?? 0);
  const g = getGrade(level);
  const { color, glow, blockH, avatarRem } = cfg;
  const avatarPx = Math.round(avatarRem * 16);
  const [imgFailed, setImgFailed] = useState(false);
  const isP1 = cfg.rank === 1;

  return (
    <div className="flex-1 flex flex-col items-center cursor-pointer select-none" onClick={onClick}>
      {isP1 && <style>{P1_STYLES}</style>}

      {/* Crown (P1 only) */}
      {isP1 && (
        <div className="mb-1 relative" style={{ animation: 'crown-float 2.6s ease-in-out infinite, crown-glow 2.6s ease-in-out infinite' }}>
          <CrownSVG />
        </div>
      )}
      {!isP1 && <div style={{ height: 32 }} />}

      {/* Avatar */}
      <div className="relative mb-2">
        {/* Sparkles around P1 avatar */}
        {isP1 && SPARKLE_POS.map((pos, i) => (
          <div key={i} style={{
            position: 'absolute', width: 5, height: 5, borderRadius: '50%',
            background: '#FFD600', ...pos,
            animation: `sparkle 1.8s ${pos.delay} ease-in-out infinite`,
          }} />
        ))}
        <div className="rounded-full overflow-hidden shrink-0 flex items-center justify-center"
          style={{
            width: avatarPx, height: avatarPx,
            border: `${isP1 ? 3 : 2.5}px solid ${color}`,
            animation: isP1 ? 'p1-ring 2.6s ease-in-out infinite' : undefined,
            boxShadow: isP1 ? undefined : `0 0 16px ${glow}`,
          }}>
          {entry.user.avatar && !imgFailed
            ? <img src={entry.user.avatar} alt={entry.user.username}
                className="w-full h-full object-cover"
                onError={() => setImgFailed(true)} />
            : <div className="w-full h-full flex items-center justify-center text-sm font-black"
                style={{ background: `${color}22`, color }}>
                {(entry.user.username || '?').slice(0, 2).toUpperCase()}
              </div>
          }
        </div>
        {isMe && (
          <span className="absolute -top-1 -right-1 text-[9px] font-black px-1.5 py-0.5 rounded-full"
            style={{ background: '#e10600', color: '#fff', boxShadow: '0 0 6px rgba(225,6,0,0.6)' }}>Toi</span>
        )}
      </div>

      {/* Name */}
      <p className="text-xs font-black truncate w-full text-center px-1 mb-0.5"
        style={{ color: isMe ? '#e10600' : '#fff' }}>
        {entry.user.username}
      </p>

      {/* Grade */}
      <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded mb-2"
        style={{ color: g.color, background: g.bg }}>{g.label} · Niv. {level}</span>

      {/* Points */}
      <div className="mb-3 text-center">
        <span className="font-black text-base" style={{ color }}>{entry.totalScore}</span>
        <span className="text-zinc-500 text-[10px] ml-0.5">pts</span>
        {entry.racesPlayed > 0 && (
          <p className="text-[9px] text-zinc-600 mt-0.5">{entry.racesPlayed} GP</p>
        )}
      </div>

      {/* Podium block */}
      <div className="w-full rounded-t-xl flex flex-col items-center justify-start pt-2 relative overflow-hidden"
        style={{ height: blockH, background: `linear-gradient(160deg, ${color}28 0%, ${color}10 60%, transparent 100%)`, border: `1px solid ${color}44`, borderBottom: 'none' }}>
        {/* P1 shimmer sweep */}
        {isP1 && (
          <div className="absolute inset-0 pointer-events-none" style={{
            background: 'linear-gradient(105deg, transparent 30%, rgba(255,214,0,0.18) 50%, transparent 70%)',
            backgroundSize: '200% 100%',
            animation: 'p1-block-shimmer 2.2s ease-in-out infinite',
          }} />
        )}
        <div className="w-6 h-0.5 rounded-full mb-1" style={{ background: color + 'aa' }} />
        <span className="font-black text-sm" style={{ color: color + 'cc' }}>P{cfg.rank}</span>
        {/* Shimmer line */}
        <div className="absolute bottom-0 left-0 right-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${color}66, transparent)` }} />
      </div>
    </div>
  );
};

const medal = (rank) => {
  const c = rank === 1 ? '#FFD600' : rank === 2 ? '#A8A8B3' : rank === 3 ? '#CD7F32' : null;
  return c
    ? <span className="font-black text-sm" style={{ color: c }}>P{rank}</span>
    : <span className="text-zinc-500 font-medium text-sm">{rank}</span>;
};

const ClassementsTab = () => {
  const { user } = useAuth();
  const [board, setBoard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [season] = useState(new Date().getFullYear());
  const [selectedEntry, setSelectedEntry] = useState(null);

  useEffect(() => {
    api.get(`/leaderboard?season=${season}`)
      .then(r => setBoard(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [season]);

  if (loading) return <div className="flex items-center justify-center h-48 text-zinc-400">Chargement...</div>;

  const top3 = board.slice(0, 3);
  const rest  = board.slice(3);
  // Podium display order: P2 · P1 · P3
  const podiumOrder = [top3[1], top3[0], top3[2]];
  const podiumCfg   = [PODIUM_CFG[0], PODIUM_CFG[1], PODIUM_CFG[2]];

  return (
    <div className="space-y-4">
      {board.length === 0 ? (
        <div className="card text-center text-zinc-400 py-12">
          <p className="text-lg mb-2">Aucun score enregistré pour l'instant.</p>
          <p className="text-sm">Les scores sont calculés automatiquement après chaque course.</p>
        </div>
      ) : (
        <>
          {/* ── Podium top 3 ── */}
          <div className="card overflow-hidden pb-0 pt-5">
            {/* Season label */}
            <p className="text-center text-[10px] uppercase tracking-[0.2em] text-zinc-600 font-bold mb-4">
              Championnat {season}
            </p>
            <div className="flex items-end gap-1.5 px-2">
              {podiumOrder.map((entry, i) =>
                entry ? (
                  <PodiumSlot
                    key={entry.user._id}
                    entry={entry}
                    cfg={podiumCfg[i]}
                    isMe={!!(user && entry.user._id === user.id)}
                    onClick={() => setSelectedEntry({ entry, rank: podiumCfg[i].rank })}
                  />
                ) : (
                  <div key={i} className="flex-1" />
                )
              )}
            </div>
          </div>

          {/* ── P4+ list ── */}
          {rest.length > 0 && (
            <div className="card p-0 overflow-hidden divide-y divide-zinc-800/60">
              {rest.map((entry, idx) => {
                const rank = idx + 4;
                const isMe = user && entry.user._id === user.id;
                const { level: lvl } = getLevelInfo(entry.user?.xp ?? 0);
                const g = getGrade(lvl);
                return (
                  <div key={entry.user._id}
                    onClick={() => setSelectedEntry({ entry, rank })}
                    className={`flex items-center gap-2.5 px-3 py-3 transition-colors cursor-pointer ${isMe ? 'bg-f1red/5' : 'hover:bg-zinc-800/30'}`}>
                    <div className="w-6 text-center shrink-0">{medal(rank)}</div>
                    <UserAvatar user={entry.user} size={8} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className={`font-bold text-sm truncate ${isMe ? 'text-f1red' : 'text-white'}`}>
                          {entry.user.username}
                        </span>
                        {isMe && <span className="text-[10px] text-f1red bg-f1red/10 px-1.5 py-0.5 rounded shrink-0">Toi</span>}
                      </div>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className="text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded"
                          style={{ color: g.color, background: g.bg }}>{g.label} · Niv. {lvl}</span>
                        {entry.racesPlayed > 0 && <span className="text-[10px] text-zinc-600">{entry.racesPlayed} GP</span>}
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      <span className="font-black text-white">{entry.totalScore}</span>
                      <span className="text-zinc-600 text-[10px] ml-0.5">pts</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {selectedEntry && (
        <PlayerScoreModal
          entry={selectedEntry.entry}
          rank={selectedEntry.rank}
          onClose={() => setSelectedEntry(null)}
        />
      )}

      <div className="card">
        <h3 className="font-bold text-white mb-3 text-sm">Système de points</h3>
        <div className="grid grid-cols-2 gap-2 text-xs text-zinc-400">
          {[
            [<><Icon name="trophy" size={12} className="inline mr-1" />Vainqueur</>, '25 pts'],
            [<><Icon name="trophy" size={12} className="inline mr-1 text-zinc-400" />P2</>, '18 pts'],
            [<><Icon name="trophy" size={12} className="inline mr-1 text-orange-400" />P3</>, '15 pts'],
            [<><Icon name="clock" size={12} className="inline mr-1" />Meilleur tour course</>, '12 pts'],
            [<><Icon name="lightning" size={12} className="inline mr-1" />Pole position</>, '10 pts'],
            [<><Icon name="clock" size={12} className="inline mr-1" />Meilleur tour qualif</>, '8 pts'],
            ['🚗 Safety Car (oui/non)', '4 pts'],
            ['🟡 Virtual Safety Car (oui/non)', '2 pts'],
            ['🚩 Drapeau rouge (oui/non)', '10 pts'],
            ['Q1 éliminé (par pilote)', '1 pt'],
            ['Q2 éliminé (par pilote)', '2 pts'],
            ['Sprint Vainqueur', '12 pts'],
            ['Sprint P2', '10 pts'],
            ['Sprint P3', '8 pts'],
          ].map(([label, pts], i) => (
            <div key={i} className="flex justify-between gap-2">
              <span className="flex items-center gap-1">{label}</span>
              <span className="text-white font-semibold">{pts}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ── JOUEURS tab ───────────────────────────────────────────────────────────────
const JoueursTab = () => {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    api.get('/leaderboard/players').then(r => setPlayers(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = players.filter(p => p.username.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-5">
      <div className="relative">
        <Icon name="user" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <input type="text" className="input pl-9" placeholder="Rechercher un joueur..."
          value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      <p className="text-zinc-500 text-xs">{players.length} joueur{players.length > 1 ? 's' : ''} inscrit{players.length > 1 ? 's' : ''}</p>

      {loading ? (
        <div className="flex items-center justify-center h-48 text-zinc-400">Chargement...</div>
      ) : filtered.length === 0 ? (
        <div className="card text-center py-12 text-zinc-400">Aucun joueur trouvé.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {filtered.map((player) => {
            const driverObj = player.favoriteDriver
              ? (F1_DRIVERS.find(d => d.name === player.favoriteDriver.name) || null) : null;
            const rank = players.indexOf(player) + 1;
            const rankColor = rank === 1 ? 'text-yellow-400' : rank === 2 ? 'text-zinc-300' : rank === 3 ? 'text-amber-600' : 'text-zinc-600';
            return (
              <Link key={player._id} to={`/joueurs/${player._id}`} className="card-hover flex flex-col gap-3 no-underline">
                <div className="flex items-center gap-3">
                  <span className={`text-sm font-black w-6 text-center shrink-0 ${rankColor}`}>
                    {rank <= 3 ? ['🥇','🥈','🥉'][rank - 1] : `#${rank}`}
                  </span>
                  <div className="w-12 h-12 rounded-full overflow-hidden border-2 shrink-0"
                    style={{ borderColor: driverObj ? `${driverObj.color}66` : '#27272a' }}>
                    {player.avatar
                      ? <img src={player.avatar} alt={player.username} className="w-full h-full object-cover" />
                      : <div className="w-full h-full bg-f1red/20 flex items-center justify-center text-lg font-black text-f1red">
                          {player.username?.[0]?.toUpperCase()}
                        </div>
                    }
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-bold text-sm truncate">{player.username}</p>
                    {(() => { const { level: lv } = getLevelInfo(player.xp ?? 0); const g = getGrade(lv); return (
                      <span className="text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded"
                        style={{ color: g.color, background: g.bg }}>{g.label} · Niv. {lv}</span>
                    ); })()}
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-f1red font-black text-lg">{player.totalScore}</p>
                    <p className="text-zinc-600 text-[10px] uppercase tracking-wide">pts</p>
                  </div>
                </div>
                {driverObj ? (
                  <div className="flex items-center gap-2 rounded-lg px-2.5 py-1.5 border"
                    style={{ borderColor: `${driverObj.color}33`, backgroundColor: `${driverObj.color}0d` }}>
                    <div className="w-7 h-7 rounded-full overflow-hidden bg-zinc-800 shrink-0">
                      <img src={driverImgUrl(driverObj.img)} alt={driverObj.name}
                        className="w-full h-full object-cover object-top" onError={e => { e.target.style.display = 'none'; }} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-bold text-white truncate">{driverObj.name}</p>
                      <p className="text-[10px] truncate" style={{ color: driverObj.color }}>{driverObj.team}</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 rounded-lg px-2.5 py-1.5 border border-zinc-800">
                    <Icon name="user" size={12} className="text-zinc-600 shrink-0" />
                    <p className="text-zinc-600 text-xs">Pas de pilote favori</p>
                  </div>
                )}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── AMIS tab ──────────────────────────────────────────────────────────────────
const ScoreLabel = ({ score, isWinner }) => {
  if (score == null) return <span className="text-zinc-500 text-xs">En attente</span>;
  return <span className={`text-sm font-black ${isWinner === true ? 'text-green-400' : isWinner === false ? 'text-red-400' : 'text-zinc-400'}`}>{score} pts</span>;
};

const ChallengeCard = ({ challenge, myId, onAccept, onDecline }) => {
  const isChallenger = String(challenge.challenger?._id) === String(myId);
  const opponent = isChallenger ? challenge.challenged : challenge.challenger;
  const myScore = isChallenger ? challenge.challengerScore : challenge.challengedScore;
  const opScore  = isChallenger ? challenge.challengedScore : challenge.challengerScore;
  const isWinner = challenge.winner ? String(challenge.winner._id ?? challenge.winner) === String(myId) : null;
  const isDraw   = challenge.status === 'completed' && !challenge.winner && myScore != null;

  const statusBadge = {
    pending:   isChallenger
      ? <span className="text-xs bg-zinc-700/60 text-zinc-400 px-2 py-0.5 rounded-full">En attente</span>
      : <span className="text-xs bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 px-2 py-0.5 rounded-full animate-pulse">Défi reçu !</span>,
    accepted:  <span className="text-xs bg-blue-500/20 text-blue-400 border border-blue-500/30 px-2 py-0.5 rounded-full">En cours</span>,
    declined:  <span className="text-xs bg-zinc-700 text-zinc-500 px-2 py-0.5 rounded-full">Refusé</span>,
    completed: <span className={`text-sm font-black ${isWinner ? 'text-green-400' : isDraw ? 'text-zinc-400' : 'text-red-400'}`}>
      {isWinner ? 'Victoire' : isDraw ? 'Égalité' : 'Défaite'}
    </span>,
  }[challenge.status];

  return (
    <div className={`card flex flex-col gap-3 ${challenge.status === 'pending' && !isChallenger ? 'border-yellow-500/30 bg-yellow-500/5' : ''} ${challenge.status === 'completed' && isWinner ? 'border-green-500/20 bg-green-500/5' : ''}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">GP · R{challenge.race?.round} · {challenge.race?.raceName}</p>
          <p className="text-white text-sm font-bold truncate">
            {isChallenger ? 'Tu as défié ' : 'Défi de '}
            <span className="text-f1red">{opponent?.username}</span>
          </p>
        </div>
        <div className="shrink-0 text-right">{statusBadge}</div>
      </div>
      {challenge.status !== 'completed' && challenge.status !== 'declined' && (
        <p className="text-[11px] text-zinc-500 bg-zinc-800/50 rounded-lg px-3 py-1.5">
          Qui marque le plus de points pronostic sur ce GP ? · Gagnant = <span className="text-yellow-400 font-bold">+1 000 XP</span>
        </p>
      )}
      {challenge.status === 'completed' && (
        <div className="grid grid-cols-3 items-center gap-2 bg-zinc-800/50 rounded-xl px-3 py-2.5">
          <div className="text-center">
            <p className="text-[10px] text-zinc-500 mb-0.5">Toi</p>
            <ScoreLabel score={myScore} isWinner={isWinner === true} />
          </div>
          <div className="text-center"><span className="text-zinc-600 text-xs font-black">VS</span></div>
          <div className="text-center">
            <p className="text-[10px] text-zinc-500 mb-0.5">{opponent?.username}</p>
            <ScoreLabel score={opScore} isWinner={isWinner === false && !isDraw} />
          </div>
        </div>
      )}
      {challenge.status === 'pending' && !isChallenger && (
        <div className="flex gap-2">
          <button onClick={() => onAccept(challenge._id)} className="flex-1 btn-primary py-2 text-xs">Accepter le défi</button>
          <button onClick={() => onDecline(challenge._id)} className="flex-1 btn-secondary py-2 text-xs">Refuser</button>
        </div>
      )}
    </div>
  );
};

const AmisTab = () => {
  const { user } = useAuth();
  const { friends, fetchFriends, removeFriend } = useFriends() || {};
  const [challenges, setChallenges] = useState([]);
  const [unread, setUnread] = useState({});
  const [chatFriend, setChatFriend] = useState(null);
  const [challengeFriend, setChallengeFriend] = useState(null);
  const [subTab, setSubTab] = useState('friends');

  const fetchChallenges = useCallback(() => {
    api.get('/challenges').then(r => setChallenges(r.data)).catch(() => {});
  }, []);

  const fetchUnread = useCallback(() => {
    api.get('/messages/unread-count').then(r => setUnread(r.data.bySender || {})).catch(() => {});
  }, []);

  useEffect(() => {
    fetchFriends?.();
    fetchChallenges();
    fetchUnread();
    const iv = setInterval(() => { fetchUnread(); fetchChallenges(); }, 15000);
    return () => clearInterval(iv);
  }, []);

  const pendingChallenges = challenges.filter(c => c.status === 'pending' && String(c.challenged?._id) === String(user?.id));

  if (!user) return (
    <div className="card text-center py-12 text-zinc-400">
      <p className="mb-3">Connecte-toi pour voir tes amis.</p>
      <Link to="/login" className="btn-primary px-6 py-2">Se connecter</Link>
    </div>
  );

  return (
    <div className="space-y-5">
      <div className="flex gap-1 bg-zinc-900 border border-zinc-800 rounded-xl p-1 w-fit">
        {[
          { key: 'friends', label: `Amis (${friends?.length ?? 0})` },
          { key: 'challenges', label: `Défis${pendingChallenges.length > 0 ? ` 🔴` : ` (${challenges.length})`}` },
        ].map(t => (
          <button key={t.key} onClick={() => setSubTab(t.key)}
            className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-all ${subTab === t.key ? 'bg-zinc-700 text-white' : 'text-zinc-500 hover:text-zinc-300'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {subTab === 'friends' && (
        <div className="flex flex-col gap-4">
          {!friends?.length ? (
            <div className="card text-center py-12">
              <p className="text-zinc-500 text-sm mb-2">Aucun ami pour l'instant.</p>
              <p className="text-zinc-600 text-xs">Trouve des joueurs dans l'onglet Joueurs →</p>
            </div>
          ) : friends.map(friend => {
            const unreadCount = unread[friend._id] || 0;
            const { level } = getLevelInfo(friend.xp ?? 0);
            return (
              <div key={friend._id} className="card flex items-center gap-4">
                <Link to={`/joueurs/${friend._id}`} className="shrink-0">
                  <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-zinc-700 hover:border-zinc-500 transition-colors">
                    {friend.avatar
                      ? <img src={friend.avatar} alt="" className="w-full h-full object-cover" />
                      : <div className="w-full h-full bg-f1red/20 flex items-center jusborder-yellow-500/30 border bg-yellow-500/5f1red">
                          {friend.username?.[0]?.toUpperCase()}
                        </div>}
                  </div>
                </Link>
                <div className="flex-1 min-w-0">
                  <Link to={`/joueurs/${friend._id}`} className="text-white font-bold text-base hover:text-f1red transition-colors truncate block">
                    {friend.username}
                  </Link>
                  {(() => { const g = getGrade(level); return (
                    <span className="text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded"
                      style={{ color: g.color, background: g.bg }}>{g.label} · Niv. {level}</span>
                  ); })()}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => setChatFriend(friend)}
                    className="relative btn-secondary text-xs py-1.5 px-3 flex items-center gap-1.5">
                    <Icon name="message" size={12} />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1.5 -right-1.5 bg-f1red text-white text-[9px] font-black w-4 h-4 rounded-full flex items-center justify-center">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </button>
                  <button onClick={() => setChallengeFriend(friend)} className="btn-secondary text-xs py-1.5 px-3"><Icon name="sword" size={12} /></button>
                  <button onClick={() => removeFriend?.(friend._id)}
                    className="text-zinc-600 hover:text-red-400 text-xs py-1.5 px-2 rounded-lg transition-colors border border-transparent hover:border-red-900/40">✕</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {subTab === 'challenges' && (
        <div className="flex flex-col gap-4">
          {challenges.length === 0 ? (
            <div className="card text-center py-12">
              <p className="text-zinc-500 text-sm mb-2">Aucun défi en cours.</p>
            </div>
          ) : challenges.map(c => (
            <ChallengeCard key={c._id} challenge={c} myId={user?.id}
              onAccept={async id => { await api.patch(`/challenges/${id}/accept`); fetchChallenges(); }}
              onDecline={async id => { await api.patch(`/challenges/${id}/decline`); fetchChallenges(); }} />
          ))}
        </div>
      )}

      {chatFriend && <ChatModal friend={chatFriend} onClose={() => { setChatFriend(null); fetchUnread(); }} />}
      {challengeFriend && <ChallengeModal friend={challengeFriend} onClose={() => setChallengeFriend(null)} onSent={fetchChallenges} />}
    </div>
  );
};

// ── Main Paddock page ─────────────────────────────────────────────────────────
const TABS = [
  { key: 'classements', label: 'Classements' },
  { key: 'joueurs',     label: 'Joueurs' },
  { key: 'amis',        label: 'Amis' },
];

const Paddock = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'classements';
  const setTab = (key) => setSearchParams({ tab: key });

  return (
    <div className="max-w-5xl mx-auto px-4 py-10">
      {/* Header */}
      <div className="mb-8">
        <p className="text-zinc-500 text-xs uppercase tracking-widest font-medium mb-1">Social · Stats</p>
        <h1 className="text-3xl font-black text-white">Le Paddock</h1>
        <p className="text-zinc-400 text-sm mt-1">Classements, joueurs et amis au même endroit.</p>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 bg-zinc-900 border border-zinc-800 rounded-xl p-1 mb-8 w-fit">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${
              activeTab === t.key ? 'bg-f1red text-white shadow-sm' : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {activeTab === 'classements' && <ClassementsTab />}
      {activeTab === 'joueurs'     && <JoueursTab />}
      {activeTab === 'amis'        && <AmisTab />}
    </div>
  );
};

export default Paddock;
